/**
 * Matomo tracking — defensive facade.
 *
 * SAFETY CONTRACT (see docs/matomo-tracking-plan.md):
 *  - NEVER throws to the caller: every public method is wrapped in try/catch.
 *  - Fire-and-forget: only pushes onto the async `_paq` queue, never awaits.
 *  - No-op when disabled or when the Matomo snippet is absent (adblock / CSP /
 *    dev), so the app behaves identically whether tracking succeeds, fails or
 *    is off. Tracking must not be able to break or slow down the application.
 *
 * "Enabled" is decided once by public/index.html (which also loads matomo.js)
 * and exposed as `window.__okvTracking`. A build-time
 * VUE_APP_TRACKING_ENABLED='false' hard-kills it regardless.
 */

const isBrowser = typeof window !== 'undefined'

const isDev = (() => {
  try {
    return process.env.NODE_ENV === 'development'
  } catch (e) {
    return false
  }
})()

function warn(e) {
  // Only surface issues in dev; in prod the facade stays completely silent.
  if (isDev) {
    // eslint-disable-next-line no-console
    console.warn('[tracking]', e)
  }
}

function isEnabled() {
  try {
    if (!isBrowser) return false
    if (process.env.VUE_APP_TRACKING_ENABLED === 'false') return false
    const cfg = window.__okvTracking
    return !!(cfg && cfg.enabled)
  } catch (e) {
    return false
  }
}

// Low-level push to the Matomo command queue. Safe even if _paq is missing.
function push(args) {
  try {
    if (!isEnabled()) return
    window._paq = window._paq || []
    window._paq.push(args)
  } catch (e) {
    warn(e)
  }
}

function trim(value, max) {
  try {
    if (value == null) return undefined
    const s = String(value)
    const limit = max || 150
    return s.length > limit ? s.slice(0, limit) : s
  } catch (e) {
    return undefined
  }
}

// Non-cryptographic, stable pseudonymisation (djb2) so we never send PII
// (email / username) in clear to the analytics backend.
function pseudonymize(input) {
  try {
    const str = String(input || '')
    if (!str) return undefined
    let h = 5381
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) + h + str.charCodeAt(i)) | 0
    }
    return 'u' + (h >>> 0).toString(36)
  } catch (e) {
    return undefined
  }
}

/**
 * Custom dimension slot ids. They must be created in Matomo FIRST; until the
 * real ids are configured here they stay null and `dimension()` is a no-op, so
 * we never push data against a wrong/undefined slot.
 */
export const DIMENSION = {
  // Matomo custom dimension ids (visit scope). `realm` = tenant, dimension id 1
  // on all three sites (prod/test/dev) → per-tenant segmentation (option 1).
  // role / assetClass not created yet on Matomo → keep null (dimension() no-op).
  realm: 1,
  role: null,
  assetClass: null,
}

// Guards the once-per-session interim "Tenant" event (option 2).
let tenantSent = false

// Page-load budget for the "under-2s / over-2s" KPI event.
export const PERF_BUDGET_MS = 2000

// Monotonic clock with fallback.
function nowMs() {
  try {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : new Date().getTime()
  } catch (e) {
    return null
  }
}

// Full initial page-load time from the Navigation Timing API (ms), or null.
function navigationLoadMs() {
  try {
    if (typeof performance === 'undefined') return null
    if (performance.getEntriesByType) {
      const nav = performance.getEntriesByType('navigation')[0]
      if (nav && nav.duration > 0) return nav.duration
    }
    const t = performance.timing
    if (t && t.loadEventEnd && t.navigationStart && t.loadEventEnd >= t.navigationStart) {
      return t.loadEventEnd - t.navigationStart
    }
    return null
  } catch (e) {
    return null
  }
}

export const track = {
  pageView(url, title) {
    try {
      if (url) push(['setCustomUrl', trim(url, 500)])
      if (title) push(['setDocumentTitle', trim(title, 200)])
      push(['trackPageView'])
      // Re-scan the freshly rendered DOM for outbound links / downloads.
      push(['enableLinkTracking'])
    } catch (e) {
      warn(e)
    }
  },

  event(category, action, name, value) {
    try {
      const args = ['trackEvent', trim(category, 60), trim(action, 60)]
      if (name != null) args.push(trim(name, 150))
      if (typeof value === 'number' && isFinite(value)) args.push(value)
      push(args)
    } catch (e) {
      warn(e)
    }
  },

  setUser(rawId) {
    try {
      const id = pseudonymize(rawId)
      if (id) push(['setUserId', id])
    } catch (e) {
      warn(e)
    }
  },

  clearUser() {
    try {
      push(['resetUserId'])
    } catch (e) {
      warn(e)
    }
  },

  dimension(id, value) {
    try {
      if (id == null) return // slot not configured yet → skip
      push(['setCustomDimension', id, trim(value, 150)])
    } catch (e) {
      warn(e)
    }
  },

  // Tenant (realm) tracking with a single Site ID.
  //  - Option 1 (ready): visit-scope custom dimension → no-op until
  //    DIMENSION.realm holds a real id, then it enables per-tenant segmentation.
  //  - Option 2 (interim, active now): one "Tenant" event per session so the
  //    "most active tenants" report works today without any Matomo admin config.
  tenant(realm) {
    try {
      if (!realm) return
      track.dimension(DIMENSION.realm, realm)
      if (!tenantSent) {
        tenantSent = true
        track.event('Tenant', 'active', realm)
      }
    } catch (e) {
      warn(e)
    }
  },

  // Page-load performance vs the 2s budget. Fires a "Perf" event with the
  // measured milliseconds as value → lets Matomo show "% under 2s" (and a Goal
  // on the 'under-2s' event) alongside the native Page Performance report.
  perf(ms) {
    try {
      if (typeof ms !== 'number' || !isFinite(ms) || ms < 0) return
      const rounded = Math.round(ms)
      track.event('Perf', rounded < PERF_BUDGET_MS ? 'under-2s' : 'over-2s', undefined, rounded)
    } catch (e) {
      warn(e)
    }
  },

  // Measures the initial full page load (Navigation Timing) once available and
  // reports it via perf(). Safe to call early (defers to window 'load').
  measureInitialLoad() {
    try {
      const run = () => {
        try {
          const ms = navigationLoadMs()
          if (ms != null) track.perf(ms)
        } catch (e) {
          warn(e)
        }
      }
      if (typeof document !== 'undefined' && document.readyState === 'complete') {
        run()
      } else if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('load', run, { once: true })
      }
    } catch (e) {
      warn(e)
    }
  },
}

/**
 * Declarative click tracking:
 *  - directive:  v-track="'Category/action/name'"  or
 *                v-track="{ category, action, name, value }"
 *  - delegated:  any element (or ancestor) carrying data-track-category
 *                [+ data-track-action / data-track-name / data-track-value]
 *
 * Both keep component LOGIC untouched (markup annotations only) and route every
 * call through the defensive facade.
 */
export function installTrackingDirectives(Vue) {
  try {
    Vue.directive('track', {
      bind(el, binding) {
        try {
          el.__okvTrackHandler = function () {
            try {
              const v = binding.value
              if (typeof v === 'string') {
                const p = v.split('/')
                track.event(p[0], p[1], p[2])
              } else if (v && typeof v === 'object') {
                track.event(v.category, v.action, v.name, v.value)
              }
            } catch (e) {
              warn(e)
            }
          }
          el.addEventListener('click', el.__okvTrackHandler)
        } catch (e) {
          warn(e)
        }
      },
      unbind(el) {
        try {
          if (el.__okvTrackHandler) {
            el.removeEventListener('click', el.__okvTrackHandler)
            el.__okvTrackHandler = null
          }
        } catch (e) {
          /* ignore */
        }
      },
    })

    if (isBrowser && typeof document !== 'undefined' && document.addEventListener) {
      // Capture phase + guarded traversal so a stopPropagation in app code still
      // lets us see the click, and a malformed node can never throw upward.
      document.addEventListener(
        'click',
        function (ev) {
          try {
            let node = ev.target
            let hops = 0
            while (node && node !== document && hops < 25) {
              if (node.getAttribute && node.getAttribute('data-track-category')) {
                const raw = node.getAttribute('data-track-value')
                const num = raw != null ? Number(raw) : NaN
                track.event(
                  node.getAttribute('data-track-category'),
                  node.getAttribute('data-track-action') || 'click',
                  node.getAttribute('data-track-name'),
                  isFinite(num) ? num : undefined
                )
                break
              }
              node = node.parentNode
              hops++
            }
          } catch (e) {
            warn(e)
          }
        },
        true
      )
    }
  } catch (e) {
    warn(e)
  }
}

// Exposes this.$track(category, action, name, value) on every component.
export function installTrackingPlugin(Vue) {
  try {
    Vue.prototype.$track = function (category, action, name, value) {
      track.event(category, action, name, value)
    }
  } catch (e) {
    warn(e)
  }
}
